import type { ApiError as ApiErrorBody } from '@ceo/shared';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, string[] | undefined>;

  constructor(status: number, body?: ApiErrorBody) {
    super(body?.error.message ?? `Request failed with status ${status}`);
    this.status = status;
    this.code = body?.error.code ?? 'http_error';
    this.details = body?.error.details as ApiError['details'];
  }
}

type Options = Omit<RequestInit, 'body'> & {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
};

/** Small fetch wrapper: JSON in/out, cookies included, typed errors. */
export async function api<T>(
  path: string,
  { body, query, headers, ...init }: Options = {},
): Promise<T> {
  const url = new URL(`/api/v1${path}`, window.location.origin);
  for (const [k, v] of Object.entries(query ?? {}))
    if (v !== undefined) url.searchParams.set(k, String(v));

  const isForm = body instanceof FormData;
  const res = await fetch(url, {
    credentials: 'same-origin',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined && !isForm ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
  });

  if (res.status === 204) return undefined as T;
  const data = res.headers.get('content-type')?.includes('application/json')
    ? await res.json()
    : undefined;
  if (!res.ok) throw new ApiError(res.status, data);
  return data as T;
}
